import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { EM_DASH } from "@/lib/utils";

import { fetchQualityCaseActivity } from "./casesApi";

function formatWhen(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function QualityCaseActivityCard({ caseId }: { caseId: number }): JSX.Element {
  const activityQuery = useQuery({
    queryKey: ["quality", "case", caseId, "activity"],
    queryFn: () => fetchQualityCaseActivity(caseId),
    enabled: Number.isFinite(caseId) && caseId > 0,
  });
  const rows = activityQuery.data?.rows ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Notes, calibration results, and updates already stored on this case and the records linked to it, oldest first.">
          Case activity
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        {activityQuery.isLoading ? <p className="text-sm text-slate-600">Loading activity…</p> : null}
        {activityQuery.isError ? (
          <p className="text-sm text-red-700">{(activityQuery.error as Error).message}</p>
        ) : null}
        {!activityQuery.isLoading && !activityQuery.isError && rows.length === 0 ? (
          <p className="text-sm text-slate-600">No activity on this case yet.</p>
        ) : null}
        {rows.length > 0 ? (
          <ol className="space-y-3">
            {rows.map((row) => (
              <li key={`${row.recordType}-${row.recordId}-${row.occurredAt}`} className="text-sm">
                <p className="text-xs text-slate-500">{formatWhen(row.occurredAt)}</p>
                <p>
                  <Link className="font-medium text-sky-700 hover:underline" to={row.href}>
                    {row.recordLabel}
                  </Link>
                </p>
                <p className="whitespace-pre-wrap text-slate-800">{row.text || EM_DASH}</p>
              </li>
            ))}
          </ol>
        ) : null}
      </CardBody>
    </Card>
  );
}
